import React from "react";
import { Drawer, IconButton } from "@mui/material";
import { MdClose } from "react-icons/md";
import Cookies from "universal-cookie";
import { NAME } from "../../../../config";

const cookies = new Cookies();

const CommentDrawer = ({
  isOpen,
  onClose,
  activeCommentKey,
  title = "Comments",
  subtitle = "Investment Proof Comments",
  comments = [],
  onAddComment,
  hasMoreComments = false,
  onLoadMoreComments,
  isAddingComment = false,
  isLoadingComments = false,
  // New props for controlled input
  commentText = "",
  onCommentTextChange,
  isCommentInputFocused = false,
  onCommentInputFocus,
  onCommentInputBlur,
  onCancelComment,
}) => {
  // Get current user name from cookies; show "--" if not present
  const currentUserName = cookies.get(NAME) || "--";

  // Helper function to get background color based on first letter
  const getAvatarColor = (name) => {
    const colors = [
      'bg-red-200 text-red-800',
      'bg-blue-200 text-blue-800',
      'bg-green-200 text-green-800',
      'bg-yellow-200 text-yellow-800',
      'bg-purple-200 text-purple-800',
      'bg-pink-200 text-pink-800',
      'bg-indigo-200 text-indigo-800',
      'bg-gray-200 text-gray-800',
    ];
    
    const firstChar = (name || "").charAt(0).toUpperCase();
    const index = firstChar ? firstChar.charCodeAt(0) % colors.length : 7;
    return colors[index];
  };

  // Helper function to get initials; return "--" verbatim when name is "--"
  const getInitials = (name) => {
    if (!name) return "--";
    if (name === "--") return "--";
    return name.charAt(0).toUpperCase();
  };

  // Update handlers to use parent callbacks
  const handleCommentInputFocus = () => {
    onCommentInputFocus?.();
  };

  const handleCommentInputBlur = () => {
    onCommentInputBlur?.();
  };

  const handleSaveComment = () => {
    if (commentText.trim() && onAddComment) {
      onAddComment(commentText.trim());
      // Don't reset here - parent will handle it after API success
    }
  };

  const handleCancelComment = () => {
    onCancelComment?.();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 400,
          padding: 0,
        },
      }}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <IconButton onClick={handleClose} size="small">
            <MdClose className="w-5 h-5" />
          </IconButton>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 overflow-y-auto">
          <div className="mb-5">
            <p className="text-sm text-black font-semibold mt-1">{title}</p>
            <p className="text-sm text-black">{subtitle}</p>
          </div>

          {/* Comment Input */}
          <div className="flex items-start space-x-3 mb-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getAvatarColor(currentUserName)}`}>
                <span className="text-sm font-medium">{getInitials(currentUserName)}</span>
              </div>
            </div>

            {/* Comment Input */}
            <div className="flex-1">
              <textarea
                value={commentText}
                onChange={(e) => onCommentTextChange?.(e.target.value)}
                onFocus={handleCommentInputFocus}
                onBlur={handleCommentInputBlur}
                placeholder="Enter your comments here..."
                disabled={isAddingComment}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm placeholder:text-gray-400 placeholder:italic focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all ${
                  isCommentInputFocused || commentText.trim()
                    ? "min-h-[120px]"
                    : "min-h-[40px]"
                } ${isAddingComment ? "opacity-50" : ""}`}
                style={{
                  fontSize: "14px",
                }}
              />

              {/* Action buttons - only show when focused or has content */}
              {(isCommentInputFocused || commentText.trim()) && (
                <div className="flex justify-end space-x-2 mt-3">
                  <button
                    onClick={handleCancelComment}
                    disabled={isAddingComment}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveComment}
                    disabled={!commentText.trim() || isAddingComment}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingComment ? "Saving..." : "Save"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Loading indicator for initial load */}
          {isLoadingComments && comments.length === 0 && (
            <div className="flex justify-center py-4">
              <div className="text-sm text-gray-500">Loading comments...</div>
            </div>
          )}

          {/* Comments list */}
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getAvatarColor(comment.firstName || comment.author)}`}>
                    <span className="text-sm font-medium">{comment.initials ?? getInitials(comment.firstName || comment.author)}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-sm text-gray-800">{comment.text}</p>
                  </div>
                  <div className="flex items-center mt-1 text-xs text-gray-500">
                    <span>by {comment.author}</span>
                    <span className="mx-2">•</span>
                    <span>{comment.timestamp}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMoreComments && (
            <div className="flex justify-center mt-6">
              <button
                onClick={onLoadMoreComments}
                disabled={isLoadingComments}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingComments ? "Loading..." : "Load More Comments"}
              </button>
            </div>
          )}

          {/* No comments message */}
          {!isLoadingComments && comments.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No comments yet.</p>
              <p className="text-xs text-gray-400 mt-1">Be the first to add a comment!</p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CommentDrawer;
